import { useMemo } from 'react';
import { Typography, Box, Grid, Paper, Fade } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { currencyParser } from '../../../../helper/parser';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import StraightenIcon from '@mui/icons-material/Straighten';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(4),
		marginTop: theme.spacing(3),
		borderRadius: '20px',
		backgroundImage: 'linear-gradient(120deg, #ffffff 0%, #f5f7fa 100%)',
		boxShadow: '0 15px 35px rgba(0, 0, 0, 0.1)',
		overflow: 'hidden',
		position: 'relative',
		width: '100%',
		'&::before': {
			content: '""',
			position: 'absolute',
			top: 0,
			left: 0,
			width: '100%',
			height: '6px',
			background: 'linear-gradient(90deg, #ff4d4d, #ff9999)'
		}
	},
	firstTitle: {
		textAlign: 'center',
		fontWeight: 800,
		marginBottom: theme.spacing(3),
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		width: '100%',
		color: '#2d3748',
		fontSize: '2.2rem',
		letterSpacing: '0.5px',
		textTransform: 'uppercase',
		background: 'black',
		WebkitBackgroundClip: 'text',
		WebkitTextFillColor: 'transparent'
	},
	firseetTitle: {
		margin: '0.4rem 0 1.2rem',
		paddingBottom: 0,
		display: 'flex',
		alignItems: 'center',
		gap: '0.5rem',
		fontWeight: 600
	},
	titleIcon: {
		fontSize: '1.8rem',
		color: '#ff4d4d',
		alignSelf: 'center'
	},
	title: {
		margin: '2.2rem 0 1rem',
		paddingBottom: 0
	},
	propertyDetailsGrid: {
		width: '100%',
		margin: '0 auto'
	},
	propertyDetailCard: {
		padding: '1rem',
		marginBottom: '1rem',
		borderRadius: '16px',
		backgroundColor: 'white',
		transition: 'all 0.3s ease',
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
		position: 'relative',
		overflow: 'hidden',
		boxShadow: '0 4px 15px rgba(0, 0, 0, 0.04)',
		'&:hover': {
			transform: 'translateY(-5px)',
			boxShadow: '0 10px 25px rgba(255, 77, 77, 0.15)',
			'& $propertyIcon': {
				transform: 'scale(1.1)'
			}
		},
		'&::after': {
			content: '""',
			position: 'absolute',
			top: 0,
			left: 0,
			width: '5px',
			height: '100%',
			background: 'linear-gradient(to bottom, #ff4d4d, #ff9999)',
			borderTopLeftRadius: '16px',
			borderBottomLeftRadius: '16px'
		}
	},
	detailLabel: {
		fontSize: '1rem',
		fontWeight: 500,
		color: '#637381',
		display: 'flex',
		alignItems: 'center',
		gap: '0.7rem',
		marginBottom: '0.5rem'
	},
	detailValue: {
		fontSize: '1.25rem',
		fontWeight: 700,
		color: '#253858',
		marginTop: '0.5rem',
		letterSpacing: '0.3px'
	},
	propertyIcon: {
		color: '#ff4d4d',
		fontSize: '1.4rem',
		transition: 'transform 0.3s ease'
	},
	paperWrapper: {
		borderRadius: '20px',
		boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
		overflow: 'hidden',
		transition: 'all 0.3s ease',
		background: 'rgba(255, 255, 255, 0.8)',
		backdropFilter: 'blur(10px)',
		'&:hover': {
			boxShadow: '0 10px 30px rgba(0,0,0,0.08)'
		}
	}
}));

const getAssessmentLink = (searchToken) => {
	return (
		<>
			Assessment{' '}
			<a
				target="_blank"
				rel="noopener noreferrer"
				href={`https://www.bcassessment.ca/Property/Info/${searchToken}`}
				style={{
					verticalAlign: 'middle',
					display: 'inline-flex',
					marginLeft: '4px'
				}}
			>
				<OpenInNewIcon style={{ fontSize: '1rem' }} />
			</a>
		</>
	);
};

export default function LeadDetailTab({ listing }) {
	const classes = useStyles();

	const propertyDetails = useMemo(() => {
		if (!listing) return [];

		return [
			{
				label: listing.searchToken ? getAssessmentLink(listing.searchToken) : 'Assessment',
				value: listing.assessment ? `${currencyParser(listing.assessment)}` : '',
				icon: <AttachMoneyIcon className={classes.propertyIcon} />
			},
			{
				label: 'Year Built',
				value: listing.YrBlt,
				icon: <CalendarTodayIcon className={classes.propertyIcon} />
			},
			...(listing.FrontageFeet && Math.round(listing.FrontageFeet) !== 0
				? [
						{
							label: 'Frontage',
							value: `${Math.round(listing.FrontageFeet)} ft`,
							icon: <StraightenIcon className={classes.propertyIcon} />
						}
				  ]
				: []),
			...(listing.LotSzSqFt && Math.round(listing.LotSzSqFt) !== 0
				? [
						{
							label: 'Land size',
							value: `${Math.round(listing.LotSzSqFt)} sqft`,
							icon: <AspectRatioIcon className={classes.propertyIcon} />
						}
				  ]
				: [])
		];
	}, [listing]);

	const renderPropertyDetails = () => {
		return propertyDetails
			.filter((detail) => detail.value)
			.map((detail, index) => (
				<Grid item xs={12} sm={6} md={4} key={detail.label}>
					<Fade in={true} style={{ transitionDelay: `${index * 100}ms` }}>
						<Box className={classes.propertyDetailCard}>
							<Typography className={classes.detailLabel} component="h6">
								{detail.icon}
								{detail.label}
							</Typography>
							<Typography className={classes.detailValue} component="div">
								{detail.value}
							</Typography>
						</Box>
					</Fade>
				</Grid>
			));
	};

	return (
		<Box className={classes.root}>
			<Typography variant="h4" component="h4" className={classes.firstTitle}>
				<span style={{ flexGrow: 1 }}>Property Details</span>
			</Typography>
			<Box style={{ padding: '1rem' }}>
				<Grid container spacing={1} className={classes.propertyDetailsGrid}>
					{renderPropertyDetails()}
				</Grid>
			</Box>
		</Box>
	);
}
